import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, BookOpen, HandHeart, Sparkles, Scale, Eye, Handshake, Rocket, Wallet, MessageSquareQuote, UsersRound, MailPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

const principleIcons = [ShieldCheck, Scale, Eye, Sparkles, BookOpen, Handshake, HandHeart];

export default function Home() {
  const { t, lang } = useI18n();
  const [aggregates, setAggregates] = useState(null);
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [tr, pr, ts] = await Promise.all([
          api.get('/transparency'), api.get('/projects'), api.get('/testimonials'),
        ]);
        setAggregates(tr.data.aggregates);
        setProjects(pr.data.slice(0, 3));
        setTestimonials(ts.data.slice(0, 2));
      } catch {}
    })();
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background pointer-events-none" />
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-20 dark:opacity-30" style={{ background: 'radial-gradient(closest-side, #C5A059, transparent)' }} />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-30 dark:opacity-40" style={{ background: 'radial-gradient(closest-side, #122640, transparent)' }} />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32">
          <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-6">
            {t.hero.eyebrow}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-balance max-w-4xl font-medium">
            {t.hero.title.split('\n').map((line, i) => <span key={i} className="block">{line}</span>)}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
            {t.hero.subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link to="/help" data-testid="hero-cta-help"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity">
              {t.hero.cta_help}
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/collaborators" data-testid="hero-cta-offer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border text-foreground font-medium hover:bg-secondary transition-colors" style={{ borderColor: '#C5A059' }}>
              {t.hero.cta_offer}
            </Link>
            <Link to="/manifesto" data-testid="hero-cta-discover"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-muted-foreground hover:text-foreground font-medium transition-colors">
              {t.hero.cta_discover} <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Results counter */}
      {aggregates && (
        <section className="py-16 border-t border-border" data-testid="results-strip">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: t.transparency.users, value: aggregates.users_count },
              { label: t.transparency.requests, value: aggregates.requests_count },
              { label: t.transparency.resolved, value: aggregates.resolved_count },
              { label: t.transparency.donations, value: `€${aggregates.donations_total.toLocaleString()}` },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-display text-3xl md:text-4xl font-medium tracking-tight">{s.value}</div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* PRINCIPI */}
      <section className="py-24 lg:py-32 border-t border-border" data-testid="principles-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Manifesto</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium leading-tight">{t.principles.title}</h2>
            <p className="mt-4 text-muted-foreground text-lg">{t.principles.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border rounded-3xl overflow-hidden">
            {t.principles.items.map((p, i) => {
              const Icon = principleIcons[i % principleIcons.length];
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }} transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="bg-background p-8 lg:p-10 group" data-testid={`principle-card-${i}`}>
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-brand-deep dark:text-brand-gold-dark mb-6 group-hover:scale-105 transition-transform duration-300">
                    <Icon size={20} strokeWidth={1.75} />
                  </div>
                  <h3 className="font-display text-xl font-medium tracking-tight mb-2">{p.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{p.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECTS teaser */}
      {projects.length > 0 && (
        <section className="py-24 lg:py-32 border-t border-border" data-testid="projects-teaser">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-end justify-between mb-12 gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4"><Rocket size={12} className="inline mr-1" /> {t.nav.projects}</p>
                <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium leading-tight">{t.projects.title}</h2>
                <p className="mt-3 text-muted-foreground text-lg">{t.projects.subtitle}</p>
              </div>
              <Link to="/projects" className="hidden sm:inline-flex items-center gap-2 text-brand-deep dark:text-brand-gold-dark font-medium hover:gap-3 transition-all">
                {t.projects.title} <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {projects.map((p) => (
                <Link key={p.id} to={`/projects/${p.id}`} className="block p-7 rounded-3xl border border-border bg-card hover:-translate-y-1 transition-all duration-300">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{t.projects.status[p.status]}</div>
                  <h3 className="font-display text-lg font-medium tracking-tight">{lang === 'it' ? p.title_it : p.title_en}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">{lang === 'it' ? p.summary_it : p.summary_en}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reliability explainer */}
      <section className="py-24 lg:py-32 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Verificabilità</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium leading-tight">Ogni contenuto è etichettato per attendibilità.</h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
              Non mescoliamo mai fatti verificati con opinioni, testimonianze, ipotesi e interpretazioni. Ogni articolo della biblioteca mostra chiaramente il suo livello — così puoi decidere consapevolmente.
            </p>
            <Link to="/library" data-testid="cta-library"
              className="inline-flex items-center gap-2 mt-8 text-brand-deep dark:text-brand-gold-dark font-medium hover:gap-3 transition-all">
              Esplora la biblioteca <ArrowRight size={18} />
            </Link>
          </div>
          <div className="space-y-3">
            {[
              { level: 'verified_fact', label: t.reliability.verified_fact, desc: 'Fonti primarie, verificabili in modo indipendente.' },
              { level: 'testimonial', label: t.reliability.testimonial, desc: 'Racconto personale, valore soggettivo.' },
              { level: 'opinion', label: t.reliability.opinion, desc: 'Punto di vista, non un fatto.' },
              { level: 'hypothesis', label: t.reliability.hypothesis, desc: 'Da approfondire, non ancora conclusiva.' },
              { level: 'interpretation', label: t.reliability.interpretation, desc: 'Lettura argomentata di fatti noti.' },
              { level: 'unverified_data', label: t.reliability.unverified_data, desc: 'Da trattare con cautela.' },
            ].map((r) => (
              <div key={r.level} className="flex items-start gap-4 p-5 rounded-2xl border border-border bg-card hover:border-foreground/20 transition-colors">
                <span className="mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: getReliabilityColor(r.level) }} />
                <div>
                  <div className="font-medium text-foreground">{r.label}</div>
                  <div className="text-sm text-muted-foreground">{r.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials preview */}
      {testimonials.length > 0 && (
        <section className="py-24 lg:py-32 border-t border-border" data-testid="testimonials-preview">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="max-w-2xl mb-12">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4"><MessageSquareQuote size={12} className="inline mr-1" /> {t.nav.testimonials}</p>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium leading-tight">{t.testimonials.title}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {testimonials.map((tt) => (
                <div key={tt.id} className="p-8 rounded-3xl border border-border bg-card">
                  <p className="text-lg leading-relaxed text-foreground/90">"{tt.body}"</p>
                  <div className="mt-4 pt-4 border-t border-border text-sm">
                    <span className="font-medium">{tt.author_name}</span>
                    {tt.role && <span className="text-muted-foreground"> · {tt.role}</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/testimonials" className="inline-flex items-center gap-2 text-brand-deep dark:text-brand-gold-dark font-medium">
                {t.testimonials.title} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Quick nav to all Phase 2 pages */}
      <section className="py-24 lg:py-32 border-t border-border" data-testid="explore-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Esplora</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium leading-tight mb-14">Tutto ciò che Tikkun mette a disposizione.</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { to: '/library', label: t.nav.library, icon: BookOpen },
              { to: '/help', label: t.nav.help, icon: HandHeart },
              { to: '/projects', label: t.nav.projects, icon: Rocket },
              { to: '/collaborators', label: t.nav.collaborators, icon: UsersRound },
              { to: '/transparency', label: t.nav.transparency, icon: Wallet },
              { to: '/assistant', label: t.nav.assistant, icon: Sparkles },
              { to: '/testimonials', label: t.nav.testimonials, icon: MessageSquareQuote },
              { to: '/contact', label: t.nav.contact, icon: MailPlus },
              { to: '/results', label: t.results.title, icon: ShieldCheck },
            ].map((x) => (
              <Link key={x.to} to={x.to} data-testid={`explore-${x.label.toLowerCase()}`}
                className="group flex items-center justify-between gap-4 p-6 rounded-2xl border border-border bg-card hover:border-foreground/20 hover:-translate-y-0.5 transition-all">
                <div className="flex items-center gap-3">
                  <x.icon size={18} strokeWidth={1.5} className="text-muted-foreground group-hover:text-brand-gold-dark transition-colors" />
                  <span className="font-medium">{x.label}</span>
                </div>
                <ArrowRight size={16} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-24 lg:py-32 border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium leading-tight text-balance">
            Un piccolo gesto verificato vale più di mille opinioni.
          </h2>
          <p className="mt-6 text-muted-foreground text-lg max-w-2xl mx-auto">
            Chiedi supporto o offri le tue competenze. Ogni azione documentata è un passo verso una società più trasparente.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/help" data-testid="final-cta-help"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90">
              {t.hero.cta_help} <ArrowRight size={18} />
            </Link>
            <Link to="/register" data-testid="final-cta-join"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border font-medium hover:bg-secondary">
              {t.nav.register}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function getReliabilityColor(level) {
  return {
    verified_fact: '#10B981', testimonial: '#3B82F6', opinion: '#64748B',
    hypothesis: '#8B5CF6', interpretation: '#0EA5E9', unverified_data: '#F59E0B',
  }[level];
}
